import React, { useRef, useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import Editor, { Monaco } from '@monaco-editor/react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../contexts/AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

// Default strategy template with proper structure
const DEFAULT_STRATEGY = `# Strategiz Python Trading Strategy
# This strategy uses a simple moving average crossover system

import numpy as np
import pandas as pd

# Strategy parameters - these can be modified
SHORT_WINDOW = 20
LONG_WINDOW = 50
SYMBOL = 'SPY'  # Default symbol

class Strategy:
    def __init__(self, data):
        self.data = data
        self.positions = []
        self.equity_curve = []
        
    def run(self):
        """Main strategy execution logic"""
        # Calculate indicators
        self.data['short_ma'] = self.data['close'].rolling(window=SHORT_WINDOW).mean()
        self.data['long_ma'] = self.data['close'].rolling(window=LONG_WINDOW).mean()
        
        # Generate signals
        self.data['signal'] = 0
        self.data.loc[self.data['short_ma'] > self.data['long_ma'], 'signal'] = 1  # Buy signal
        self.data.loc[self.data['short_ma'] < self.data['long_ma'], 'signal'] = -1  # Sell signal
        
        # Calculate position changes
        self.data['position'] = self.data['signal'].shift(1)
        self.data['position'].fillna(0, inplace=True)
        
        # Calculate strategy returns
        self.data['returns'] = self.data['close'].pct_change()
        self.data['strategy_returns'] = self.data['position'] * self.data['returns']
        
        # Calculate cumulative returns
        self.data['cumulative_returns'] = (1 + self.data['returns']).cumprod()
        self.data['strategy_cumulative_returns'] = (1 + self.data['strategy_returns']).cumprod()
        
        # Generate trades list
        trades = []
        position = 0
        
        for i, row in self.data.iterrows():
            if row['position'] == 1 and position <= 0:
                # Buy signal
                trades.append({
                    'date': i,
                    'type': 'buy',
                    'price': row['close'],
                    'qty': 1
                })
                position = 1
            elif row['position'] == -1 and position >= 0:
                # Sell signal
                trades.append({
                    'date': i,
                    'type': 'sell',
                    'price': row['close'],
                    'qty': 1
                })
                position = -1
        
        return {
            'data': self.data,
            'trades': trades,
            'final_value': self.data['strategy_cumulative_returns'].iloc[-1] if len(self.data) > 0 else 1.0,
            'max_drawdown': self.calculate_max_drawdown()
        }
    
    def calculate_max_drawdown(self):
        """Calculate maximum drawdown of the strategy"""
        if len(self.data) == 0 or 'strategy_cumulative_returns' not in self.data:
            return 0
            
        # Calculate drawdown
        cumulative = self.data['strategy_cumulative_returns']
        running_max = cumulative.cummax()
        drawdown = (cumulative / running_max) - 1
        return drawdown.min()

# This function will be called by the backtesting engine
def create_strategy(data):
    return Strategy(data)
`;

interface PyStrategyEditorProps {
  strategyId?: string;
  onSave?: (strategyId: string, code: string) => void;
  onRun?: (code: string) => void;
}

export const PyStrategyEditor: React.FC<PyStrategyEditorProps> = ({ 
  strategyId,
  onSave,
  onRun
}) => {
  const { state } = useAuth();
  const editorRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState<string>(DEFAULT_STRATEGY);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    if (strategyId && state.user) {
      loadStrategy();
    }
  }, [strategyId, state.user]);

  function handleEditorDidMount(editor: any, monaco: Monaco) {
    editorRef.current = editor;
    
    // Add Python language features
    monaco.languages.registerCompletionItemProvider('python', {
      provideCompletionItems: (model, position) => {
        // Create a range based on the current cursor position
        const wordInfo = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          startColumn: wordInfo.startColumn,
          endLineNumber: position.lineNumber,
          endColumn: wordInfo.endColumn
        };

        const suggestions = [
          {
            label: 'np',
            kind: monaco.languages.CompletionItemKind.Module,
            insertText: 'np',
            detail: 'NumPy module (for numerical operations)',
            range: range
          },
          {
            label: 'pd',
            kind: monaco.languages.CompletionItemKind.Module,
            insertText: 'pd',
            detail: 'Pandas module (for data manipulation)',
            range: range
          },
          {
            label: 'import numpy as np',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'import numpy as np',
            detail: 'Import NumPy module',
            range: range
          },
          {
            label: 'import pandas as pd',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'import pandas as pd',
            detail: 'Import Pandas module',
            range: range
          },
          {
            label: 'def',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'def ${1:function_name}(${2:parameters}):\n\t${3:pass}',
            detail: 'Define a new function',
            range: range
          },
          {
            label: 'if',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'if ${1:condition}:\n\t${2:pass}',
            detail: 'If statement',
            range: range
          },
          {
            label: 'for',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'for ${1:item} in ${2:items}:\n\t${3:pass}',
            detail: 'For loop',
            range: range
          },
          {
            label: 'class',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'class ${1:ClassName}:\n\tdef __init__(self, ${2:parameters}):\n\t\t${3:pass}',
            detail: 'Define a new class',
            range: range
          }
        ];
        
        return { suggestions };
      }
    });
  }
  
  const loadStrategy = async () => {
    if (!state.user?.id || !strategyId) return;
    
    try {
      setLoading(true);
      const strategyDoc = await getDoc(doc(db, `users/${state.user.id}/strategies/${strategyId}`));
      
      if (strategyDoc.exists()) {
        const strategyData = strategyDoc.data();
        if (strategyData && strategyData.code) {
          setCode(strategyData.code);
        }
      }
    } catch (error) {
      console.error('Error loading strategy:', error);
      setNotification({
        type: 'error',
        message: 'Failed to load strategy'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!state.user?.id) {
      setNotification({
        type: 'error',
        message: 'You must be logged in to save a strategy'
      });
      return;
    }
    
    try {
      setSaving(true);
      const currentCode = editorRef.current.getValue();
      
      // Create a new ID if one doesn't exist
      const docId = strategyId || `strategy_${Date.now()}`;
      const strategyRef = doc(db, `users/${state.user.id}/strategies/${docId}`);
      
      await setDoc(strategyRef, {
        code: currentCode,
        createdAt: strategyId ? undefined : new Date(),
        updatedAt: new Date(),
        name: docId,
        status: 'DRAFT'
      }, { merge: true });
      
      setNotification({
        type: 'success',
        message: 'Strategy saved successfully'
      });
      
      if (onSave) {
        onSave(docId, currentCode);
      }
    } catch (error) {
      console.error('Error saving strategy:', error);
      setNotification({
        type: 'error',
        message: 'Failed to save strategy'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleRun = () => {
    if (!editorRef.current) return;
    
    const currentCode = editorRef.current.getValue();
    if (onRun) {
      onRun(currentCode);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <Paper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Python Strategy Editor</Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={handleRun}
            sx={{ mr: 1 }}
          >
            Run Backtest
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Strategy'}
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Editor
            height="100%"
            defaultLanguage="python"
            defaultValue={code}
            value={code}
            options={{
              minimap: { enabled: true },
              scrollBeyondLastLine: false,
              fontSize: 14,
              wordWrap: 'on'
            }}
            onMount={handleEditorDidMount}
            onChange={(value) => setCode(value || '')}
          />
        )}
      </Box>
      
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notification ? (
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.type}
            variant="filled"
          >
            {notification.message}
          </Alert>
        ) : <></>}
      </Snackbar>
    </Paper>
  );
};

export default PyStrategyEditor;
